import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  maxWidth?: string;
  resizable?: boolean;
  initialHeight?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "max-w-4xl",
  resizable = true,
  initialHeight
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const startPos = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!modalRef.current || !resizable) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = modalRef.current.getBoundingClientRect();
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height,
    };
    setIsResizing(true);
  }, [resizable]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!startPos.current) return;

      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;

      const newWidth = Math.max(400, startPos.current.width + deltaX);
      const newHeight = Math.max(300, startPos.current.height + deltaY);

      // Limit to viewport
      const maxWidth = window.innerWidth - 32;
      const maxHeight = window.innerHeight - 32;

      setSize({
        width: Math.min(newWidth, maxWidth),
        height: Math.min(newHeight, maxHeight),
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      startPos.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);

      // Prevent layout shift by compensating for scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [isOpen, onClose]);

  // Reset size when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSize(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className={`relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg flex flex-col ${!size ? `w-full ${maxWidth}` : ''}`}
          style={size ? { width: size.width, height: size.height } : { height: initialHeight, maxHeight: 'calc(100vh - 80px)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1 font-inter">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-b-xl flex-1 overflow-auto flex flex-col">
            {children}
          </div>

          {/* Resize handle */}
          {resizable && (
            <div
              className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize z-10"
              onMouseDown={handleMouseDown}
            >
              <div className="absolute bottom-1 right-1 w-4 h-4">
                <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                  <path
                    d="M22 22L13 13M13 22L22 13"
                    stroke="rgba(156, 163, 175, 0.6)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;